import { Clock, Home, ShoppingCart, Check, TestTube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { TestPackage } from "@/services/data";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Props {
  pkg: TestPackage;
}

const colorMap: Record<string, string> = {
  blue: "from-blue-500/10 to-blue-600/5 border-blue-200",
  purple: "from-purple-500/10 to-purple-600/5 border-purple-200",
  red: "from-red-500/10 to-red-600/5 border-red-200",
  orange: "from-orange-500/10 to-orange-600/5 border-orange-200",
  pink: "from-pink-500/10 to-pink-600/5 border-pink-200",
  green: "from-green-500/10 to-green-600/5 border-green-200",
};

const badgeColorMap: Record<string, string> = {
  blue: "bg-blue-100 text-blue-700",
  purple: "bg-purple-100 text-purple-700",
  red: "bg-red-100 text-red-700",
  orange: "bg-orange-100 text-orange-700",
  pink: "bg-pink-100 text-pink-700",
  green: "bg-green-100 text-green-700",
};

export default function PackageCard({ pkg }: Props) {
  const { addPackage, hasItem } = useCart();
  const navigate = useNavigate();
  const inCart = hasItem(pkg.id);
  const discount = Math.round(((pkg.price - pkg.discountedPrice) / pkg.price) * 100);

  const handleAdd = () => {
    addPackage(pkg);
    toast.success(`${pkg.name} added to cart`);
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[pkg.color] || colorMap.blue} border rounded-xl p-5 hover:shadow-lg transition-all flex flex-col`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          {pkg.popular && (
            <Badge className={`${badgeColorMap[pkg.color]} border-0 text-xs mb-2`}>Most Popular</Badge>
          )}
          <h3 className="font-bold text-foreground leading-snug mb-1">{pkg.name}</h3>
          <p className="text-xs text-muted-foreground">{pkg.tagline}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
        <span className="flex items-center gap-1"><TestTube className="h-3 w-3" />{pkg.testCount} tests</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{pkg.turnaround}</span>
        {pkg.homeCollection && <span className="flex items-center gap-1 text-green-600"><Home className="h-3 w-3" />Home</span>}
      </div>

      <div className="mt-auto">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl font-bold text-foreground">₹{pkg.discountedPrice}</span>
          <div>
            <span className="text-xs text-muted-foreground line-through block">₹{pkg.price}</span>
            <Badge className="bg-green-100 text-green-700 border-0 text-xs">{discount}% off</Badge>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 text-xs"
            variant={inCart ? "secondary" : "default"}
            onClick={inCart ? () => navigate("/booking") : handleAdd}
          >
            {inCart ? <><Check className="h-3 w-3 mr-1" />Added</> : <><ShoppingCart className="h-3 w-3 mr-1" />Add to Cart</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs"
            onClick={() => { if (!inCart) addPackage(pkg); navigate("/booking"); }}
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
}
